import React, { useEffect, useRef, useState } from 'react'
import axios from 'axios';

import './../styles/car-model-selector.css'

import { Box, Button, CircularProgress, FormControl, InputLabel, MenuItem, Select } from '@mui/material';
import { API_URL } from '../constants/urls';
import { PRODUCTS_MAP, PRODUCTS_TYPES_MAP, PRODUCT_LINES_MAP } from '../constants/product';
import { formatMoney, getMinAndMaxPriceFromVariants } from '../utils/helpers';

const DEFAULT_PRODUCT = PRODUCTS_TYPES_MAP.CAR_COVERS;

axios.defaults.headers.post['Content-Type'] = 'application/x-www-form-urlencoded';

function CarModelSelector({ product = DEFAULT_PRODUCT, selectedCar, setSelectedCar, productVariant }) {
  const [carYears, setCarYears] = useState([]);
  const [carMakes, setCarMakes] = useState([]);
  const [carModels, setCarModels] = useState([]);
  const [carSubmodels, setCarSubmodels] = useState([]);
  const [carOptions, setCarOptions] = useState([]);
  const [carYear, setCarYear] = useState((selectedCar.year || ""));
  const [carMake, setCarMake] = useState((selectedCar.make || ""));
  const [carModel, setCarModel] = useState((selectedCar.model || ""));
  const [carSubmodel, setCarSubmodel] = useState("");
  const [carOption, setCarOption] = useState("");
  const isInitialRender = useRef(true);
  const isLoadingYears = useRef(true);
  const isLoadingMakes = useRef(true);
  const isLoadingModels = useRef(true);
  const isLoadingSubmodels = useRef(true);
  const isLoading = isLoadingYears.current || isLoadingMakes.current || isLoadingModels.current || isLoadingSubmodels.current;
  const hasSelectedCar = selectedCar?.year && selectedCar?.make && selectedCar?.model && selectedCar?.submodel;
  const productLine = PRODUCT_LINES_MAP[window?.conspire_product?.handle];
  const [variantMinPrice, variantMaxPrice] = getMinAndMaxPriceFromVariants(window.conspire_product.variants);

  const resetSelections = () => {
    setCarYear("");
    setCarMake("");
    setCarMakes([]);
    setCarModel("");
    setCarModels([]);
    setCarSubmodel("");
    setCarSubmodels([]);
    setCarOption("");
    setCarOptions([]);
    setSelectedCar({});

    localStorage.removeItem('car-year');
    localStorage.removeItem('car-make');
    localStorage.removeItem('car-model');
    localStorage.removeItem('car-submodel');
    Cookies.remove('year');
    Cookies.remove('make');
    Cookies.remove('model');
    Cookies.remove('submodel');
  }

  const getCarYears = async () => {
    const params = new URLSearchParams({
      product,
      ...(productLine && { productLine })
    });

    isLoadingYears.current = true;
    const { data } = await axios.get(`${API_URL}/vehicles?${params}`)
    const year = data.find(y => y.toString() === carYear);

    if (!year) {
      setCarYear("");
    }

    // console.log('getCarYears data', data);
    setCarYears(data);
    isLoadingYears.current = false;
  }

  const getCarMakes = async (shouldClear = true) => {
    if (shouldClear) {
      setCarMake("");
      setCarMakes([]);
      setCarModel("");
      setCarModels([]);
      setCarSubmodel("");
      setCarSubmodels([]);
      setCarOption("");
      setCarOptions([]);
    }

    if (carYear) {
      const params = new URLSearchParams({
        product,
        year: carYear,
        ...(productLine && { productLine })
      });

      isLoadingMakes.current = true;
      const { data } = await axios.get(`${API_URL}/vehicles?${params}`);
      const make = data.find(m => m === carMake);

      if (!make) {
        setCarMake("");
      }

      // console.log('getCarMakes data', data);
      setCarMakes(data);
      isLoadingMakes.current = false;
    }
  }

  const getCarModels = async (shouldClear = true) => {
    if (shouldClear) {
      setCarModel("");
      setCarModels([]);
      setCarSubmodel("");
      setCarSubmodels([]);
      setCarOption("");
      setCarOptions([]);
    }

    if (carYear && carMake) {
      const params = new URLSearchParams({
        product,
        year: carYear,
        make: carMake,
        ...(productLine && { productLine })
      });

      isLoadingModels.current = true;
      const { data } = await axios.get(`${API_URL}/vehicles?${params}`);
      const model = data.find(m => m === carModel);

      if (!model) {
        setCarModel("");
      }

      // console.log('getCarModels data', data);
      setCarModels(data);
      isLoadingModels.current = false;
    }

  }

  const getCarSubmodels = async (shouldClear = true) => {
    if (shouldClear) {
      setCarSubmodel("");
      setCarSubmodels([]);
      setCarOption("");
      setCarOptions([]);
    }

    if (carYear && carMake && carModel) {
      const selectedSubmodel = selectedCar?.submodel;
      const params = new URLSearchParams({
        product,
        year: carYear,
        make: carMake,
        model: carModel,
        combineTrimWithNotes: true,
        ...(productLine && { productLine })
      });

      isLoadingSubmodels.current = true;
      const { data } = await axios.get(`${API_URL}/vehicles?${params}`);
      let submodels = data;

      // console.log('getCarSubmodels submodels', submodels);
      setCarSubmodels(submodels);

      if (!!selectedSubmodel) {
        const submodel = submodels
          .find(s => s.trim === selectedSubmodel?.trim && s.part_number === selectedSubmodel?.part_number);

        if (!!submodel) {
          setSelectedCar({
            ...selectedCar,
            submodel
          });
          setCarSubmodel(submodel);
        } else if (!submodel) {
          setSelectedCar({
            ...selectedCar,
            submodel: ""
          });
          setCarSubmodel(false);
        }
      }

      isLoadingSubmodels.current = false;
    }
  }

  useEffect(() => {
    if (!isInitialRender.current) {
      getCarMakes();
    }
  }, [carYear]);

  useEffect(() => {
    if (!isInitialRender.current) {
      getCarModels();
    }
  }, [carMake]);

  useEffect(() => {
    if (!isInitialRender.current) {
      getCarSubmodels();
    }
  }, [carModel]);

  useEffect(() => {
    isInitialRender.current = false;
    getCarYears();

    if (carYear) {
      getCarMakes(false);
    } else {
      isLoadingMakes.current = false;
    }
    if (carMake) {
      getCarModels(false);
    } else {
      isLoadingModels.current = false;
    }
    if (carModel) {
      getCarSubmodels(false);
    } else {
      isLoadingSubmodels.current = false;
    }
  }, []);

  const handleCarYearChange = (e) => {
    const selectedYear = e.target.value;
    setCarYear(selectedYear);
    setSelectedCar({
      ...selectedCar,
      year: selectedYear
    })
    localStorage.setItem('car-year', selectedYear);
    Cookies.set('year', selectedYear);
  }
  const handleCarMakeChange = (e) => {
    const selectedMake = e.target.value;
    setCarMake(selectedMake);
    setSelectedCar({
      ...selectedCar,
      make: selectedMake
    })
    localStorage.setItem('car-make', selectedMake);
    Cookies.set('make', selectedMake);
  }
  const handleCarModelChange = (e) => {
    const selectedModel = e.target.value;
    setCarModel(selectedModel);
    setSelectedCar({
      ...selectedCar,
      model: selectedModel
    })
    localStorage.setItem('car-model', selectedModel);
    Cookies.set('model', selectedModel);
  }
  const handleCarSubmodelChange = (e) => {
    const selectedSubmodel = e.target.value;
    setCarSubmodel(selectedSubmodel);
    setSelectedCar({
      ...selectedCar,
      submodel: selectedSubmodel
    })

    try {
      const selectedSubmodelStringified = JSON.stringify(selectedSubmodel);

      localStorage.setItem('car-submodel', selectedSubmodelStringified);
      Cookies.set('submodel', selectedSubmodelStringified);
    } catch (e) {
      console.error('handleCarSubmodelChange error JSON.stringify submodel value e', e);
    }
  }

  if ((isInitialRender.current && isLoading) || (hasSelectedCar && isLoading)) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: '30px' }}>
        <CircularProgress sx={{ color: '#00f' }} />
      </Box>
    )
  }

  return (
    <section className="car-model-selector-container">
      {hasSelectedCar && !!carSubmodel ? (
        <div className="selected-car-wrapper">
          <div className="selected-car-container">
            <p className="selected-car">
              {selectedCar?.year} {selectedCar?.make} {selectedCar?.model} {selectedCar?.submodel?.trim}
            </p>
            <Button
              onClick={resetSelections}
              variant="contained"
              size="large"
              className="clear-selection-button"
            >
              Change
            </Button>
          </div>
          {selectedCar?.submodel && selectedCar?.submodel?.notes?.toLowerCase() !== 'none' && (
            <p className="selected-car-option">Options: {selectedCar?.submodel?.notes}</p>
          )}
          {carSubmodel?.part_number && (
            <div className="car-options-selected-container">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                <path d="M12 2C6.5 2 2 6.5 2 12S6.5 22 12 22 22 17.5 22 12 17.5 2 12 2M12 20C7.59 20 4 16.41 4 12S7.59 4 12 4 20 7.59 20 12 16.41 20 12 20M16.59 7.58L10 14.17L7.41 11.59L6 13L10 17L18 9L16.59 7.58Z" />
              </svg>
              <p className="car-options-selected-notice">
                {window?.conspire_product?.handle === PRODUCTS_MAP['custom-canine-covers'].handle ?
                  'Fits Your Vehicle'
                :
                  'Custom Fit for Your Vehicle'
                }
              </p>
            </div>
          )}
          {productVariant?.id && (
            <div className="car-option-price-container">
              {productVariant?.compare_at_price ? (
                <>
                  <p className="car-option-price car-option-price-discount">
                    {formatMoney(productVariant.price / 100)}
                  </p>
                  <p className="car-option-price car-option-price-has-discount">
                    {formatMoney(productVariant.compare_at_price / 100)}
                  </p>
                </>
              ) : (
                <p className="car-option-price">{formatMoney(productVariant.price / 100)}</p>
              )}
            </div>
          )}
          {carOptions.length > 1 && (
            <div className="car-options-container">
              {!carOption && (
                <p className="car-options-notice">
                  Please select your car to view pricing details.
                </p>
              )}
              <FormControl fullWidth className="car-model-form-control">
                <InputLabel id="car-option-label">Select Options</InputLabel>
                <Select
                  labelId="car-option-label"
                  id="car-option"
                  value={carOption?.part_number || ""}
                  label="Select Options"
                  onChange={handleCarOptionChange}
                  className="car-model-select car-option-select"
                >
                  {carOptions.map(option => (
                    <MenuItem key={option.part_number} value={option.part_number}>{option.notes}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>
          )}
          {!isInitialRender.current && !isLoading && carSubmodel === false && (
            <div className="car-options-empty-container">
              <p className="car-options-empty-notice">
                Looks like we don't have anything on hand that fits your vehicle.
                <a href="#">Click here</a> to place a custom order.
              </p>
            </div>
          )}
        </div>
      ) : (
        <>
          <FormControl fullWidth className="car-model-form-control">
            <InputLabel id="car-year-label">Select Year</InputLabel>
            <Select
              labelId="car-year-label"
              id="car-year"
              value={carYear}
              label="Select Year"
              onChange={handleCarYearChange}
              className="car-model-select car-year-select"
            >
              {carYears.map(year => (
                <MenuItem key={year} value={year}>{year}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth className="car-model-form-control">
            <InputLabel id="car-make-label">Select Make</InputLabel>
            <Select
              labelId="car-make-label"
              id="car-make"
              value={carMake}
              label="Select Make"
              onChange={handleCarMakeChange}
              className="car-model-select car-make-select"
              disabled={!carYear}
            >
              {carMakes.map(make => (
                <MenuItem key={make} value={make}>{make}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth className="car-model-form-control">
            <InputLabel id="car-model-label">Select Model</InputLabel>
            <Select
              labelId="car-model-label"
              id="car-model"
              value={carModel}
              label="Select Model"
              onChange={handleCarModelChange}
              className="car-model-select"
              disabled={!carMake}
            >
              {carModels.map(model => (
                <MenuItem key={model} value={model}>{model}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth className="car-model-form-control">
            <InputLabel id="car-submodel-label">Select Trim</InputLabel>
            <Select
              labelId="car-submodel-label"
              id="car-submodel"
              value={carSubmodel}
              label="Select Trim"
              onChange={handleCarSubmodelChange}
              className="car-model-select car-submodel-select"
              disabled={!carModel}
            >
              {carSubmodels.map(submodel => (
                <MenuItem key={submodel} value={submodel}>
                  {submodel.trim} {submodel?.notes?.toLowerCase() !== 'none' ? submodel.notes : ''}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <section className='lowest-price-container'>
            <p className="lowest-price">
              Starting at {formatMoney(variantMinPrice / 100)}
            </p>
          </section>
        </>
      )}
    </section>
  )
}

export default CarModelSelector
